import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import config from '../config/config';
import './VigilTraceability.css';

/* ── constants ──────────────────────────────────────────────────────────────── */
const SEV_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#ca8a04',
  low: '#2563eb',
  info: '#6b7280',
};

const STATUS_ICONS = {
  detected: '✅',
  partial: '⚠️',
  not_detected: '❌',
  not_applicable: '⊘',
};

const STATUS_LABELS = {
  detected: 'Detected',
  partial: 'Partial',
  not_detected: 'Not Detected',
  not_applicable: 'N/A',
};

const APPS = ['WebGoat', 'NodeGoat', 'juice-shop', 'railsgoat'];

const LANG_ICONS = {
  WebGoat: '☕',
  NodeGoat: '🟢',
  'juice-shop': '🍊',
  railsgoat: '💎',
};

const PAGE_SIZE = 25;
const SEV_ORDER = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };

/* ── animation variants ─────────────────────────────────────────────────────── */
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.4, ease: 'easeOut' },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
};

const expandVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: { opacity: 1, height: 'auto', transition: { duration: 0.2 } },
};

/* ── helpers ────────────────────────────────────────────────────────────────── */
function coverageColor(pct) {
  if (pct > 80) return '#16a34a';
  if (pct >= 50) return '#ca8a04';
  return '#dc2626';
}

function computeSummary(items) {
  if (!items || !items.length) return { totalVulns: 0, detected: 0, partial: 0, notDetected: 0, notApplicable: 0, coverageRate: 0 };
  const totalVulns = items.length;
  const detected = items.filter(i => i.detectionStatus === 'detected').length;
  const partial = items.filter(i => i.detectionStatus === 'partial').length;
  const notDetected = items.filter(i => i.detectionStatus === 'not_detected').length;
  const notApplicable = items.filter(i => i.detectionStatus === 'not_applicable').length;
  const applicable = totalVulns - notApplicable;
  const coverageRate = applicable > 0 ? Math.round(((detected + partial * 0.5) / applicable) * 100) : 0;
  return { totalVulns, detected, partial, notDetected, notApplicable, coverageRate };
}

/* ── CoverageGauge SVG ──────────────────────────────────────────────────────── */
function CoverageGauge({ value, size = 80, strokeWidth = 6, target = 80 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const color = coverageColor(value);

  // Target tick mark position at `target`%
  const targetAngle = ((target / 100) * 360 - 90) * (Math.PI / 180);
  const outerR = radius + strokeWidth / 2 + 1;
  const innerR = radius - strokeWidth / 2 - 1;
  const tx1 = size / 2 + outerR * Math.cos(targetAngle);
  const ty1 = size / 2 + outerR * Math.sin(targetAngle);
  const tx2 = size / 2 + innerR * Math.cos(targetAngle);
  const ty2 = size / 2 + innerR * Math.sin(targetAngle);

  return (
    <svg width={size} height={size} className="vigil-trace-gauge" title={`Coverage: ${value}% (target: ${target}%)`}>
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke="var(--bs-border-color)" strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
      {/* Target tick at 80% */}
      <line x1={tx1} y1={ty1} x2={tx2} y2={ty2}
        stroke="var(--accent, #6366f1)" strokeWidth={2} opacity={0.75} strokeLinecap="round" />
      <text
        x={size / 2} y={size / 2}
        textAnchor="middle" dominantBaseline="central"
        fill={color}
        style={{ fontSize: `${size * 0.22}px`, fontWeight: 800, fontFamily: 'var(--font-sans)' }}
      >
        {value}%
      </text>
    </svg>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────────── */
export default function VigilTraceability() {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [activeTab, setActiveTab] = useState('All');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [cweSearch, setCweSearch] = useState('');
  const [sortKey, setSortKey] = useState('severity');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRowId, setExpandedRowId] = useState(null);

  /* ── fetch data ────────────────────────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = activeTab !== 'All' ? { app: activeTab } : {};
      const { data } = await axios.get(`${config.BASE_URL}/vigil/traceability`, { params });
      setItems(data.items || []);
      setSummaryData(data.summary || []);
    } catch (err) {
      console.error('Failed to fetch traceability data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const loadDefaultCatalog = useCallback(async () => {
    setSeeding(true);
    try {
      await axios.post(`${config.BASE_URL}/vigil/traceability/load-defaults`);
      await fetchData();
    } catch (err) {
      console.error('Failed to load default catalog:', err);
    } finally {
      setSeeding(false);
    }
  }, [fetchData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [activeTab, statusFilter, severityFilter, cweSearch, sortKey]);

  /* ── per-app summaries ─────────────────────────────────────────────────── */
  const appSummaries = useMemo(() => {
    if (summaryData.length) {
      return APPS.map(app => {
        const found = summaryData.find(s => s.appName === app);
        return found || { appName: app, appLanguage: '', ...computeSummary([]) };
      });
    }
    return APPS.map(app => {
      const appItems = items.filter(i => i.appName === app);
      return { appName: app, appLanguage: '', ...computeSummary(appItems) };
    });
  }, [items, summaryData]);

  /* ── overall summary ───────────────────────────────────────────────────── */
  const overallSummary = useMemo(() => {
    if (!items.length && !summaryData.length) return computeSummary([]);
    if (summaryData.length) {
      const totalVulns = summaryData.reduce((s, a) => s + (a.totalVulns || 0), 0);
      const detected = summaryData.reduce((s, a) => s + (a.detected || 0), 0);
      const partial = summaryData.reduce((s, a) => s + (a.partial || 0), 0);
      const notDetected = summaryData.reduce((s, a) => s + (a.notDetected || 0), 0);
      const notApplicable = summaryData.reduce((s, a) => s + (a.notApplicable || 0), 0);
      const applicable = totalVulns - notApplicable;
      const coverageRate = applicable > 0 ? Math.round(((detected + partial * 0.5) / applicable) * 100) : 0;
      return { totalVulns, detected, partial, notDetected, notApplicable, coverageRate };
    }
    return computeSummary(items);
  }, [items, summaryData]);

  /* ── severity sort order ───────────────────────────────────────────────── */

  /* ── filtered + sorted items ───────────────────────────────────────────── */
  const filteredItems = useMemo(() => {
    let result = [...items];

    if (activeTab !== 'All') {
      result = result.filter(i => i.appName === activeTab);
    }
    if (statusFilter !== 'all') {
      result = result.filter(i => i.detectionStatus === statusFilter);
    }
    if (severityFilter !== 'all') {
      result = result.filter(i => i.severity === severityFilter);
    }
    if (cweSearch.trim()) {
      const q = cweSearch.toLowerCase().trim();
      result = result.filter(i =>
        (i.cwe || '').toLowerCase().includes(q) ||
        (i.vulnName || '').toLowerCase().includes(q) ||
        (i.owaspCategory || '').toLowerCase().includes(q) ||
        (i.vulnDescription || '').toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case 'severity':
          av = SEV_ORDER[a.severity] || 0;
          bv = SEV_ORDER[b.severity] || 0;
          break;
        case 'status':
          av = a.detectionStatus;
          bv = b.detectionStatus;
          break;
        case 'vulnerability':
          av = (a.vulnName || '').toLowerCase();
          bv = (b.vulnName || '').toLowerCase();
          break;
        case 'cwe':
          av = a.cwe || '';
          bv = b.cwe || '';
          break;
        default:
          av = a[sortKey] || '';
          bv = b[sortKey] || '';
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [items, activeTab, statusFilter, severityFilter, cweSearch, sortKey, sortDir]);

  /* ── sort handler ──────────────────────────────────────────────────────── */
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return ' ⇅';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  /* ── pagination ─────────────────────────────────────────────────────────── */
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, currentPage]);

  /* ── expand row ─────────────────────────────────────────────────────────── */
  const toggleRow = (id) => setExpandedRowId(prev => prev === id ? null : id);

  /* ── CSV export ─────────────────────────────────────────────────────────── */
  const exportToCSV = useCallback(() => {
    const headers = ['Status', 'Vulnerability', 'App', 'CWE', 'OWASP', 'Severity', 'Rule', 'Rule Pack', 'Evidence File', 'Description', 'Notes'];
    const rows = filteredItems.map(item => [
      item.detectionStatus || '',
      item.vulnName || '',
      item.appName || '',
      item.cwe || '',
      item.owaspCategory || '',
      item.severity || '',
      item.ruleId || '',
      item.rulePack || '',
      item.evidenceFile ? `${item.evidenceFile}${item.evidenceLine ? ':' + item.evidenceLine : ''}` : '',
      (item.vulnDescription || '').replace(/\n/g, ' '),
      (item.notes || '').replace(/\n/g, ' '),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `traceability-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredItems, activeTab]);

  /* ── render ────────────────────────────────────────────────────────────── */
  const hasData = items.length > 0 || summaryData.length > 0;

  return (
    <div className="vigil-trace">
      {/* ── header ─────────────────────────────────────────────────────────── */}
      <div className="vigil-trace-header">
        <div>
          <h2>📋 {t('vigilTraceability.title', 'Traceability Dashboard')}</h2>
          <p className="vigil-trace-subtitle">
            {t('vigilTraceability.subtitle', 'OWASP test-app vulnerability detection coverage by Vigil SAST rules')}
          </p>
        </div>
        <div className="vigil-trace-header-actions">
          {hasData && (
            <button
              className="vigil-trace-btn vigil-trace-btn-export"
              onClick={exportToCSV}
              title={t('vigilTraceability.exportTitle', 'Export filtered results to CSV')}
            >
              📥 {t('vigilTraceability.exportCsv', 'Export CSV')}
            </button>
          )}
          {hasData && (
            <button
              className="vigil-trace-btn vigil-trace-btn-secondary"
              onClick={loadDefaultCatalog}
              disabled={seeding}
              title={t('vigilTraceability.reloadCatalogTitle', 'Reload the built-in OWASP vulnerability catalog')}
            >
              {seeding ? '⏳ ' + t('common.loading', 'Loading...') : '🔄 ' + t('vigilTraceability.reloadCatalog', 'Reload Catalog')}
            </button>
          )}
        </div>
      </div>

      {/* ── overall stats bar ──────────────────────────────────────────────── */}
      {hasData && (
        <motion.div className="vigil-trace-overall" initial="hidden" animate="visible" variants={fadeIn}>
          <div className="vigil-trace-overall-item">
            <span className="vigil-trace-overall-value">{overallSummary.totalVulns}</span>
            <span className="vigil-trace-overall-label">{t('vigilTraceability.totalVulns', 'Total Vulnerabilities')}</span>
          </div>
          <div className="vigil-trace-overall-divider" />
          <div className="vigil-trace-overall-item">
            <span className="vigil-trace-overall-value" style={{ color: coverageColor(overallSummary.coverageRate) }}>
              {overallSummary.coverageRate}%
            </span>
            <span className="vigil-trace-overall-label">{t('vigilTraceability.overallCoverage', 'Overall Coverage')}</span>
          </div>
          <div className="vigil-trace-overall-divider" />
          <div className="vigil-trace-overall-item">
            <span className="vigil-trace-overall-breakdown">
              <span className="vigil-trace-bd-detected vigil-trace-bd-clickable"
                onClick={() => setStatusFilter(f => f === 'detected' ? 'all' : 'detected')}
                title="Filter: Detected"
              >{STATUS_ICONS.detected} {overallSummary.detected}</span>
              <span className="vigil-trace-bd-partial vigil-trace-bd-clickable"
                onClick={() => setStatusFilter(f => f === 'partial' ? 'all' : 'partial')}
                title="Filter: Partial"
              >{STATUS_ICONS.partial} {overallSummary.partial}</span>
              <span className="vigil-trace-bd-notdet vigil-trace-bd-clickable"
                onClick={() => setStatusFilter(f => f === 'not_detected' ? 'all' : 'not_detected')}
                title="Filter: Not Detected"
              >{STATUS_ICONS.not_detected} {overallSummary.notDetected}</span>
              <span className="vigil-trace-bd-na">{STATUS_ICONS.not_applicable} {overallSummary.notApplicable}</span>
            </span>
            <span className="vigil-trace-overall-label">
              {t('vigilTraceability.breakdown', 'Breakdown')}
              <span className="vigil-trace-bd-hint"> (click to filter)</span>
            </span>
          </div>
        </motion.div>
      )}

      {/* ── app summary cards ──────────────────────────────────────────────── */}
      {hasData && (
        <div className="vigil-trace-cards">
          {appSummaries.map((app, i) => (
            <motion.div
              key={app.appName}
              className={`vigil-trace-card${activeTab === app.appName ? ' active' : ''}`}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={cardVariants}
              onClick={() => setActiveTab(activeTab === app.appName ? 'All' : app.appName)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setActiveTab(activeTab === app.appName ? 'All' : app.appName)}
            >
              <div className="vigil-trace-card-top">
                <div className="vigil-trace-card-name">
                  <span className="vigil-trace-card-langicon">{LANG_ICONS[app.appName] || '📦'}</span>
                  <span>{app.appName}</span>
                  <span className="vigil-trace-card-lang">{app.appLanguage}</span>
                </div>
                <CoverageGauge value={Math.round(app.coverageRate || 0)} size={72} strokeWidth={5} />
              </div>
              <div className="vigil-trace-card-counts">
                <span className="vigil-trace-cnt detected">{STATUS_ICONS.detected} {app.detected || 0}</span>
                <span className="vigil-trace-cnt partial">{STATUS_ICONS.partial} {app.partial || 0}</span>
                <span className="vigil-trace-cnt not-detected">{STATUS_ICONS.not_detected} {app.notDetected || 0}</span>
                <span className="vigil-trace-cnt na">{STATUS_ICONS.not_applicable} {app.notApplicable || 0}</span>
              </div>
              <div className="vigil-trace-card-progress-wrap">
                <div
                  className="vigil-trace-card-progress-fill"
                  style={{ width: `${Math.min(app.coverageRate || 0, 100)}%`, background: coverageColor(app.coverageRate || 0) }}
                />
                <div className="vigil-trace-card-progress-target" title="80% target" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* ── tab navigation ─────────────────────────────────────────────────── */}
      {hasData && (
        <div className="vigil-trace-tabs">
          {['All', ...APPS].map(tab => (
            <button
              key={tab}
              className={`vigil-trace-tab${activeTab === tab ? ' active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'All' ? t('vigilTraceability.allApps', 'All') : tab}
            </button>
          ))}
        </div>
      )}

      {/* ── filter bar ─────────────────────────────────────────────────────── */}
      {hasData && (
        <>
          <div className="vigil-trace-filters">
            <input
              type="text"
              className="form-control vigil-trace-search"
              placeholder={t('vigilTraceability.searchPlaceholder', 'Search by CWE, vulnerability, or OWASP...')}
              value={cweSearch}
              onChange={e => setCweSearch(e.target.value)}
            />
            <div className="vigil-trace-chips">
              {['all', 'detected', 'partial', 'not_detected', 'not_applicable'].map(s => (
                <button
                  key={s}
                  className={`vigil-trace-chip${statusFilter === s ? ' active' : ''}`}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === 'all' ? t('vigilTraceability.allStatuses', 'All') : `${STATUS_ICONS[s]} ${STATUS_LABELS[s]}`}
                </button>
              ))}
            </div>
            <select
              className="form-select vigil-trace-sev-select"
              value={severityFilter}
              onChange={e => setSeverityFilter(e.target.value)}
            >
              <option value="all">{t('vigilTraceability.allSeverities', 'All Severities')}</option>
              {Object.keys(SEV_COLORS).map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="vigil-trace-row-count">
            Showing <strong>{filteredItems.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredItems.length)}</strong> of <strong>{filteredItems.length}</strong> vulnerabilities
            {filteredItems.length !== items.length && (
              <span className="vigil-trace-row-count-total"> ({items.length} total)</span>
            )}
          </div>
        </>
      )}

      {/* ── loading ────────────────────────────────────────────────────────── */}
      {loading && (
        <div className="vigil-trace-loading">⏳ {t('common.loading', 'Loading...')}</div>
      )}

      {/* ── error ──────────────────────────────────────────────────────────── */}
      {error && !loading && (
        <div className="vigil-trace-error">
          <span>⚠️ {t('vigilTraceability.error', 'Failed to load traceability data')}: {error}</span>
          <button className="vigil-trace-retry-btn" onClick={fetchData}>
            {t('common.retry', 'Retry')}
          </button>
        </div>
      )}

      {/* ── empty state ────────────────────────────────────────────────────── */}
      {!loading && !error && !hasData && (
        <div className="vigil-trace-empty">
          <div className="vigil-trace-empty-icon">📋</div>
          <div className="vigil-trace-empty-title">
            {t('vigilTraceability.emptyTitle', 'No traceability data yet')}
          </div>
          <div className="vigil-trace-empty-desc">
            {t('vigilTraceability.emptyDesc', 'Load the built-in OWASP vulnerability catalog to visualize Vigil rule coverage.')}
          </div>
          <button
            className="vigil-trace-btn vigil-trace-btn-primary mt-3"
            onClick={loadDefaultCatalog}
            disabled={seeding}
          >
            {seeding ? '⏳ ' + t('common.loading', 'Loading...') : '📥 ' + t('vigilTraceability.loadCatalog', 'Load OWASP Catalog')}
          </button>
        </div>
      )}

      {/* ── data table ─────────────────────────────────────────────────────── */}
      {!loading && !error && hasData && (
        <motion.div className="vigil-trace-table-wrapper" initial="hidden" animate="visible" variants={fadeIn}>
          {filteredItems.length === 0 ? (
            <div className="vigil-trace-no-results">
              {t('vigilTraceability.noResults', 'No vulnerabilities match the current filters.')}
            </div>
          ) : (
            <table className="vigil-trace-table">
              <thead>
                <tr>
                  <th style={{ width: '32px' }} />
                  <th className="sortable" onClick={() => handleSort('status')}>
                    {t('vigilTraceability.colStatus', 'Status')}<span className="vigil-trace-sort-icon">{sortIcon('status')}</span>
                  </th>
                  <th className="sortable" onClick={() => handleSort('vulnerability')}>
                    {t('vigilTraceability.colVuln', 'Vulnerability')}<span className="vigil-trace-sort-icon">{sortIcon('vulnerability')}</span>
                  </th>
                  {activeTab === 'All' && (
                    <th>{t('vigilTraceability.colApp', 'App')}</th>
                  )}
                  <th className="sortable" onClick={() => handleSort('cwe')}>
                    {t('vigilTraceability.colCwe', 'CWE')}<span className="vigil-trace-sort-icon">{sortIcon('cwe')}</span>
                  </th>
                  <th>{t('vigilTraceability.colOwasp', 'OWASP')}</th>
                  <th className="sortable" onClick={() => handleSort('severity')}>
                    {t('vigilTraceability.colSeverity', 'Severity')}<span className="vigil-trace-sort-icon">{sortIcon('severity')}</span>
                  </th>
                  <th>{t('vigilTraceability.colRule', 'Rule')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((item, idx) => {
                  const rowId = item.id || `${item.appName}-${item.vulnName}-${idx}`;
                  const isExpanded = expandedRowId === rowId;
                  const hasDetails = item.vulnDescription || item.notes || item.evidenceFile || item.evidenceSnippet;
                  const colSpan = activeTab === 'All' ? 8 : 7;
                  return (
                    <React.Fragment key={rowId}>
                      <tr
                        className={`vigil-trace-row-${item.detectionStatus}${isExpanded ? ' vigil-trace-row-open' : ''}${hasDetails ? ' vigil-trace-row-expandable' : ''}`}
                        onClick={() => hasDetails && toggleRow(rowId)}
                      >
                        {/* Expand toggle */}
                        <td className="vigil-trace-expand-cell">
                          {hasDetails && (
                            <span className={`vigil-trace-expand-icon${isExpanded ? ' open' : ''}`}>›</span>
                          )}
                        </td>

                        {/* Status */}
                        <td>
                          <span className={`vigil-trace-status vigil-trace-status-${item.detectionStatus}`}>
                            {STATUS_ICONS[item.detectionStatus] || '?'} {STATUS_LABELS[item.detectionStatus] || item.detectionStatus}
                          </span>
                        </td>

                        {/* Vulnerability name (description moves to expanded row) */}
                        <td>
                          <span className="vigil-trace-vuln-name">{item.vulnName || '—'}</span>
                        </td>

                        {/* App (only in All tab) */}
                        {activeTab === 'All' && (
                          <td>
                            <span className="vigil-trace-app-badge">
                              {LANG_ICONS[item.appName] || '📦'} {item.appName}
                            </span>
                          </td>
                        )}

                        {/* CWE */}
                        <td onClick={e => e.stopPropagation()}>
                          {item.cwe ? (
                            <a
                              href={`https://cwe.mitre.org/data/definitions/${item.cwe.replace(/\D/g, '')}.html`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="vigil-trace-cwe-badge"
                            >
                              {item.cwe}
                            </a>
                          ) : '—'}
                        </td>

                        {/* OWASP */}
                        <td>
                          {item.owaspCategory ? (
                            <span className="vigil-trace-owasp-badge">{item.owaspCategory}</span>
                          ) : '—'}
                        </td>

                        {/* Severity */}
                        <td>
                          {item.severity ? (
                            <span
                              className="vigil-trace-sev-chip"
                              style={{
                                background: `${SEV_COLORS[item.severity] || SEV_COLORS.info}18`,
                                color: SEV_COLORS[item.severity] || SEV_COLORS.info,
                              }}
                            >
                              {item.severity}
                            </span>
                          ) : '—'}
                        </td>

                        {/* Rule */}
                        <td onClick={e => e.stopPropagation()}>
                          {item.ruleId ? (
                            <div className="vigil-trace-rule-cell">
                              <a href={`/vigil/rules#${item.ruleId}`} className="vigil-trace-rule-link">
                                {item.ruleId}
                              </a>
                              {item.rulePack && (
                                <span className="vigil-trace-rule-pack">{item.rulePack}</span>
                              )}
                            </div>
                          ) : (
                            <span className="vigil-trace-no-rule">—</span>
                          )}
                        </td>
                      </tr>

                      {/* ── Expanded detail row ─────────────────────────────── */}
                      {isExpanded && (
                        <tr className="vigil-trace-detail-row">
                          <td colSpan={colSpan} className="vigil-trace-detail-td">
                            <motion.div
                              className="vigil-trace-detail-panel"
                              initial="hidden"
                              animate="visible"
                              variants={expandVariants}
                            >
                              {item.vulnDescription && (
                                <div className="vigil-trace-detail-section">
                                  <span className="vigil-trace-detail-label">Description</span>
                                  <span className="vigil-trace-detail-text">{item.vulnDescription}</span>
                                </div>
                              )}
                              {item.notes && (
                                <div className="vigil-trace-detail-section">
                                  <span className="vigil-trace-detail-label">Notes</span>
                                  <span className="vigil-trace-detail-text vigil-trace-detail-notes">{item.notes}</span>
                                </div>
                              )}
                              {item.evidenceFile && (
                                <div className="vigil-trace-detail-section">
                                  <span className="vigil-trace-detail-label">Evidence</span>
                                  <code className="vigil-trace-evidence-file">
                                    {item.evidenceFile}{item.evidenceLine ? `:${item.evidenceLine}` : ''}
                                  </code>
                                </div>
                              )}
                              {item.evidenceSnippet && (
                                <div className="vigil-trace-detail-section">
                                  <span className="vigil-trace-detail-label">Snippet</span>
                                  <pre className="vigil-trace-snippet-block"><code>{item.evidenceSnippet}</code></pre>
                                </div>
                              )}
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* ── pagination ───────────────────────────────────────────────── */}
          {totalPages > 1 && (
            <div className="vigil-trace-pagination">
              <button className="vigil-trace-page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} title="First">«</button>
              <button className="vigil-trace-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    className={`vigil-trace-page-btn${currentPage === pageNum ? ' active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >{pageNum}</button>
                );
              })}
              <button className="vigil-trace-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
              <button className="vigil-trace-page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} title="Last">»</button>
              <span className="vigil-trace-page-info">Page {currentPage} / {totalPages}</span>
            </div>
          )}
          )}
        </motion.div>
      )}
    </div>
  );
}
